import { useMemo, useState } from "react";
import { Globe } from "lucide-react";
import { useSelector } from "react-redux";
import useLawyers from "../../hooks/lawyer/useLawyers";
import LawyerCard from "../../components/lawyer/LawyerCard";
import LawyerFilterBar from "../../components/lawyer/LawyerFilterBar";
import LawyerFilterSheet from "../../components/lawyer/LawyerFilterSheet";
import LawyerActiveFilters from "../../components/lawyer/LawyerActiveFilters";
import LawyerDetail from "../../components/lawyer/LawyerDetail";
import ContactLawyerModal from "../../components/lawyer/ContactLawyerModal";
import useTranslate from "../../hooks/useTranslate";
import { resolveCountry, Flag } from "../../components/lawyer/lawyerFlag";
import {
  SearchIcon,
  ChevronDownIcon,
} from "../../components/lawyer/v2/icons";
import { LAW_SKELETON_KEYFRAMES } from "../../components/lawyer/v2/tokens";

// ============================================================================
// Lawyers directory `/lawyers` — presentation rebuilt 1:1 from the V2 design
// handoff (boards 1a default grid, 1b filtered + chips, 1c empty, 1d skeleton,
// 1k mobile directory; the card itself is LawyerCard, boards SHARED/1e/1k).
// All data flow, filtering, debounce, pending-request and modal wiring are
// byte-identical to the previous build (brief rules 6/7/8) — only the
// presentation layer changed. The detail overlay and contact modal keep the
// exact same mount contract (PKG-2 surfaces).
// ============================================================================

// One-off CSS: the handoff's skeleton pulse keyframes (board 1d) + a
// scrollbar-hide utility for the mobile filter pill strip (board 1k).
const PAGE_CSS =
  LAW_SKELETON_KEYFRAMES +
  ".law-nosb::-webkit-scrollbar{display:none}.law-nosb{-ms-overflow-style:none;scrollbar-width:none}";

// Skeleton card — exact block map from board 1d (greys #EBEFF5/#F1F4F8,
// bar radii 6, footer bar 34×r9, pulse `sk 1.4s`).
const LawyerCardSkeleton = () => (
  <div className="bg-white border border-[#E6EAF2] rounded-[14px] p-[18px] flex flex-col gap-[13px]">
    <div className="flex gap-3 items-center">
      <div className="law-sk w-[46px] h-[46px] rounded-full bg-[#EBEFF5] flex-none" />
      <div className="flex-1 flex flex-col gap-[7px]">
        <div className="law-sk w-[60%] h-[13px] rounded-[6px] bg-[#EBEFF5]" />
        <div className="law-sk w-[80%] h-[10px] rounded-[6px] bg-[#F1F4F8]" />
      </div>
    </div>
    <div className="flex gap-1.5">
      <div className="law-sk w-[96px] h-5 rounded-full bg-[#EBEFF5]" />
      <div className="law-sk w-[70px] h-5 rounded-full bg-[#F1F4F8]" />
    </div>
    <div className="law-sk w-[45%] h-[11px] rounded-[6px] bg-[#F1F4F8]" />
    <div className="flex gap-1.5">
      <div className="law-sk w-[84px] h-5 rounded-full bg-[#F1F4F8]" />
      <div className="law-sk w-[84px] h-5 rounded-full bg-[#EBEFF5]" />
    </div>
    <div className="border-t border-[#EEF1F6] pt-3">
      <div className="law-sk h-[34px] rounded-[9px] bg-[#EBEFF5]" />
    </div>
  </div>
);

// Mobile filter pill (board 1k strip). The Country pill is the promoted hero
// filter — always in the active style with a chevron, exactly as drawn; the
// other pills take the active style only while their filter is set. Every
// pill opens the filter sheet with its own section expanded (board 1l,
// "country filter one tap away").
const FilterPill = ({ label, hero = false, active = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-[7px] rounded-full text-[12.5px] flex-none whitespace-nowrap ${
      hero || active
        ? "bg-[#EBF3FF] border-[1.5px] border-[#2283FF] text-[#1567D2] font-semibold"
        : "bg-white border border-[#DDE3EC] text-[#475467] font-medium"
    }`}
  >
    {label}
    {hero && <ChevronDownIcon size={11} className="text-[#1567D2]" />}
  </button>
);

const Lawyers = () => {
  const { t } = useTranslate();
  const {
    filters,
    setFilter,
    toggleFilter,
    clearFilter,
    resetFilters,
    activeFilterCount,
    lawyers,
    facets,
    loading,
    error,
    retryFetch,
    isPending,
    markPending,
  } = useLawyers();

  const { user } = useSelector((state) => state.auth);
  const currentAccountId = user?.account_id ?? user?.id ?? null;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSection, setSheetSection] = useState("jurisdiction");
  const [detailLawyer, setDetailLawyer] = useState(null);
  const [contactLawyer, setContactLawyer] = useState(null);

  const isSelf = (l) =>
    currentAccountId != null &&
    String(l.account_id) === String(currentAccountId);

  // D-010: a sent request stays pending; flip the card + close the modal.
  const handleRequestSent = (lawyer) => {
    markPending(lawyer);
  };

  // Resolve each jurisdiction facet to a flag once, shared by bar/sheet/chips.
  const jurisdictionFlags = useMemo(() => {
    const map = {};
    facets.jurisdictions.forEach((j) => {
      map[j] = resolveCountry(j);
    });
    return map;
  }, [facets.jurisdictions]);

  const hasQueryOrFilters = !!(filters.search || activeFilterCount > 0);
  const hasChips = activeFilterCount > 0;
  // Country filter is multi-select (follow-up pack A2). The single-country
  // presentation bits (flag on the hero pill, "in X" count suffix, empty-state
  // flag bubble) render exactly as before when ONE country is selected — the
  // degenerate case — and step back gracefully with several.
  const selectedJurisdictions = filters.jurisdiction;
  const singleJurisdiction =
    selectedJurisdictions.length === 1 ? selectedJurisdictions[0] : null;
  const jurisdictionFlag = singleJurisdiction
    ? jurisdictionFlags[singleJurisdiction] ||
      resolveCountry(singleJurisdiction)
    : null;
  const jurisdictionLabel =
    jurisdictionFlag?.label || singleJurisdiction || "";

  // Chip remove: a country chip removes only ITS country from the multi
  // selection; the single-select facets clear as before.
  const removeFilterChip = (key, value) => {
    if (key === "jurisdiction") {
      toggleFilter("jurisdiction", value);
    } else {
      clearFilter(key);
    }
  };

  const openSheetAt = (section) => {
    setSheetSection(section);
    setSheetOpen(true);
  };

  // Board 1b result line: "<strong>3 lawyers</strong> in Spain" (country part
  // only when exactly ONE country is selected — the board's single-country
  // case; with several countries the plain count renders).
  const chipsCount =
    !loading && !error ? (
      <span className="text-[12.5px] text-[#667085] whitespace-nowrap">
        <strong className="text-[#101828]">
          {lawyers.length} {t("lawyers")}
        </strong>
        {singleJurisdiction ? ` ${t("in")} ${jurisdictionLabel}` : ""}
      </span>
    ) : null;

  return (
    <div className="w-full min-h-full bg-[#F5F7FB]">
      <style>{PAGE_CSS}</style>
      {/* Expanding container (Marin decision A4, 2026-07-23): the artboard's
          fixed max-w-[1280px] column was replaced by the Last-Minute expanding
          idiom — the page stretches with the viewport and the GRID below caps
          itself at very large sizes (4 cols ≥1920, 5 cols ≥2560). */}
      <div className="mx-auto px-[18px] pt-3 pb-6 md:px-8 md:pt-7 md:pb-9">
        {/* ── Desktop header row (board 1a): title+subtitle left, search right ── */}
        <div className="hidden md:flex items-end justify-between gap-5 mb-[18px]">
          <div>
            <h1 className="text-[24px] font-bold text-[#101828] tracking-[-0.2px]">
              {t("Lawyers")}
            </h1>
            <p className="text-[13.5px] text-[#667085] mt-1">
              {t(
                "Verified sports lawyers in 20+ jurisdictions. Find local counsel for your deal."
              )}
            </p>
          </div>
          {/* Directory search — the platform nav-search pattern restyled per
              the artboard (rule 11); drives the existing 300ms-debounced
              client-side filter in useLawyers. */}
          <div className="flex items-center gap-2 bg-white border border-[#DDE3EC] rounded-[10px] px-3.5 py-[9px] w-[300px] flex-none">
            <SearchIcon size={14} className="text-[#98A2B3]" />
            <input
              type="text"
              placeholder={t("Search by name or firm")}
              className="flex-1 min-w-0 text-[13px] text-[#101828] placeholder:text-[#98A2B3] bg-transparent focus:outline-none"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
            />
          </div>
        </div>

        {/* ── Mobile header block (board 1k) ── */}
        <div className="md:hidden">
          <h1 className="text-[22px] font-bold text-[#101828]">
            {t("Lawyers")}
          </h1>
          {!loading && !error && (
            <p className="text-[12.5px] text-[#667085] mt-0.5">
              {lawyers.length}{" "}
              {t(lawyers.length === 1 ? "verified lawyer" : "verified lawyers")}{" "}
              ·{" "}
              {facets.jurisdictions.length}{" "}
              {t(
                facets.jurisdictions.length === 1
                  ? "jurisdiction"
                  : "jurisdictions"
              )}
            </p>
          )}
          <div className="flex items-center gap-2 bg-white border border-[#DDE3EC] rounded-[11px] px-[13px] py-2.5 mt-3">
            <SearchIcon size={14} className="text-[#98A2B3]" />
            <input
              type="text"
              placeholder={t("Search by name or firm")}
              className="flex-1 min-w-0 text-[13px] text-[#101828] placeholder:text-[#98A2B3] bg-transparent focus:outline-none"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
            />
          </div>
          {/* Filter pill strip — horizontally scrollable (board 1k) */}
          <div className="law-nosb flex gap-[7px] mt-2.5 overflow-x-auto">
            <FilterPill
              hero
              label={
                singleJurisdiction ? (
                  <span className="inline-flex items-center gap-1.5">
                    {jurisdictionFlag?.code && (
                      <Flag {...jurisdictionFlag} width={16} height={11} />
                    )}
                    {jurisdictionLabel}
                  </span>
                ) : selectedJurisdictions.length > 1 ? (
                  // Several countries: pill shows the count (no board for
                  // this state — 1k draws single-country only)
                  // Globe as the Lucide icon, not an emoji character (T050-1)
                  <span className="inline-flex items-center gap-1.5">
                    <Globe size={13} className="flex-none" />
                    {`${t("Country")} · ${selectedJurisdictions.length}`}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <Globe size={13} className="flex-none" />
                    {t("Country")}
                  </span>
                )
              }
              onClick={() => openSheetAt("jurisdiction")}
            />
            <FilterPill
              label={t("Specialization")}
              active={!!filters.specialization}
              onClick={() => openSheetAt("specialization")}
            />
            <FilterPill
              label={t("Language")}
              active={!!filters.language}
              onClick={() => openSheetAt("language")}
            />
            <FilterPill
              label={t("City")}
              active={!!filters.city}
              onClick={() => openSheetAt("city")}
            />
          </div>
        </div>

        {/* ── Desktop filter row (boards 1a/1b; skeleton bars per 1d) ── */}
        {loading ? (
          <div className="hidden md:flex gap-2.5 mb-[22px]">
            <div className="law-sk w-[190px] h-9 rounded-[10px] bg-[#E4E9F1]" />
            <div className="law-sk w-[130px] h-9 rounded-[10px] bg-[#E4E9F1]" />
            <div className="law-sk w-[110px] h-9 rounded-[10px] bg-[#E4E9F1]" />
          </div>
        ) : (
          <div
            className={`hidden md:flex items-center gap-2.5 ${
              hasChips ? "mb-3" : "mb-[22px]"
            }`}
          >
            <LawyerFilterBar
              facets={facets}
              filters={filters}
              setFilter={setFilter}
              jurisdictionFlags={jurisdictionFlags}
              t={t}
            />
            {/* Board 1a result counter (right of the filter row; board 1b
                moves the count into the chips row instead) */}
            {!error && !hasChips && (
              <p className="ml-auto self-center text-[12.5px] text-[#98A2B3] whitespace-nowrap">
                {lawyers.length}{" "}
                {t(
                  lawyers.length === 1 ? "verified lawyer" : "verified lawyers"
                )}
              </p>
            )}
          </div>
        )}

        {/* ── Active filter chips row (boards 1b/1c) ── */}
        {hasChips && (
          <div className="mt-3 md:mt-0 mb-5">
            <LawyerActiveFilters
              filters={filters}
              onRemove={removeFilterChip}
              onReset={resetFilters}
              jurisdictionFlags={jurisdictionFlags}
              rightSlot={chipsCount}
              t={t}
            />
          </div>
        )}

        {/* ── Grid ──
            sm:auto-rows-fr → every card in a multi-column row shares the row
            height, so the bottom-anchored Contact button lines up across cards
            regardless of how much each lawyer exposes. Single-column mobile
            keeps natural per-card height (board 1k list). ≥1920 the grid grows
            to 4 columns (5 at ≥2560) and self-caps its width — the Last-Minute
            pattern (LastMinutePage.jsx grid idiom, decision A4). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 min-[1920px]:grid-cols-4 min-[2560px]:grid-cols-5 sm:auto-rows-fr gap-3 md:gap-4 w-full min-[1920px]:max-w-[1800px] min-[2560px]:max-w-[2160px] mx-auto mt-3.5 md:mt-0">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <LawyerCardSkeleton key={index} />
            ))
          ) : error ? (
            <div className="col-span-full text-center py-16">
              <p className="text-[#B42318] text-sm">{error}</p>
              <button
                onClick={retryFetch}
                className="mt-4 px-[18px] py-[9px] rounded-[9px] bg-[#2283FF] hover:bg-[#1570E8] text-white text-[13px] font-semibold"
              >
                {t("Try Again")}
              </button>
            </div>
          ) : lawyers.length === 0 ? (
            /* Empty state — board 1c (jurisdiction-specific copy + flag bubble
               when a country filter is set; the pre-rebuild generic copy
               otherwise). Buttons only when there is something to reset —
               existing gate. */
            <div className="col-span-full bg-white border border-dashed border-[#D5DBE7] rounded-[16px] px-8 py-16 flex flex-col items-center gap-2 text-center">
              <div className="relative w-[88px] h-[88px] rounded-full bg-[#EEF3FB] flex items-center justify-center mb-2">
                <SearchIcon size={34} className="text-[#8FB6EC]" />
                {jurisdictionFlag?.code && (
                  <span className="absolute -right-0.5 bottom-0.5 w-[30px] h-[30px] rounded-full bg-white border border-[#E6EAF2] flex items-center justify-center">
                    <Flag {...jurisdictionFlag} width={20} height={15} />
                  </span>
                )}
              </div>
              <h3 className="text-[17px] font-semibold text-[#101828]">
                {selectedJurisdictions.length > 0
                  ? t("No lawyers in this jurisdiction yet")
                  : t("No lawyers found")}
              </h3>
              <p className="text-[13.5px] text-[#667085] max-w-[420px] leading-[1.5]">
                {selectedJurisdictions.length > 0
                  ? t(
                      "We're onboarding lawyers in new markets. Try a neighbouring country, or clear your filters to browse all jurisdictions."
                    )
                  : t("Try adjusting your search or filters")}
              </p>
              {hasQueryOrFilters && (
                <div className="flex gap-2.5 mt-3.5">
                  <button
                    onClick={resetFilters}
                    className="px-[18px] py-[9px] rounded-[9px] bg-[#2283FF] hover:bg-[#1570E8] text-white text-[13px] font-semibold whitespace-nowrap"
                  >
                    {t("Reset filters")}
                  </button>
                  {selectedJurisdictions.length > 0 && (
                    <button
                      onClick={() => clearFilter("jurisdiction")}
                      className="px-[18px] py-[9px] rounded-[9px] bg-white border border-[#DDE3EC] text-[#344054] text-[13px] font-semibold whitespace-nowrap"
                    >
                      {t("Browse all countries")}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            lawyers.map((lawyer) => (
              <LawyerCard
                key={lawyer.account_id}
                lawyer={lawyer}
                isSelf={isSelf(lawyer)}
                isPending={isPending(lawyer)}
                onView={setDetailLawyer}
                onContact={setContactLawyer}
              />
            ))
          )}
        </div>
      </div>

      {/* Mobile filter sheet */}
      <LawyerFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        facets={facets}
        filters={filters}
        setFilter={setFilter}
        toggleFilter={toggleFilter}
        resetFilters={resetFilters}
        jurisdictionFlags={jurisdictionFlags}
        resultCount={lawyers.length}
        initialSection={sheetSection}
        t={t}
      />

      {/* Detail overlay (side-panel desktop / full-screen mobile) */}
      {detailLawyer && (
        <LawyerDetail
          lawyer={detailLawyer}
          isSelf={isSelf(detailLawyer)}
          isPending={isPending(detailLawyer)}
          onClose={() => setDetailLawyer(null)}
          onContact={(l) => setContactLawyer(l)}
          t={t}
        />
      )}

      {/* Contact-request modal / sheet */}
      {contactLawyer && (
        <ContactLawyerModal
          lawyer={contactLawyer}
          onClose={() => setContactLawyer(null)}
          onSent={handleRequestSent}
          t={t}
        />
      )}
    </div>
  );
};

export default Lawyers;
